import React, { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Dice5, PenTool, Printer, Cable, Heart, Gem, Sparkles, Flower2, Scissors, UtensilsCrossed } from 'lucide-react';
import { Button } from './Button';
interface FilterSectionProps {
  onFilterChange: (filters: any) => void;
  resultCount?: number;
  selectedTypes: string[];
  onTypeChange: (types: string[]) => void;
  selectedThemes: string[];
  onThemeChange: (themes: string[]) => void;
  isOuderKind: boolean;
  onOuderKindChange: (value: boolean) => void;
  isEngelstalig: boolean;
  onEngelstaligChange: (value: boolean) => void;
}
export function FilterSection({
  onFilterChange,
  resultCount,
  selectedTypes,
  onTypeChange,
  selectedThemes,
  onThemeChange,
  isOuderKind,
  onOuderKindChange,
  isEngelstalig,
  onEngelstaligChange
}: FilterSectionProps) {
  const [currentMonth, setCurrentMonth] = useState(1);
  const [currentYear, setCurrentYear] = useState(2026);
  const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  const themes = [{
    name: 'Draad',
    icon: <Cable size={20} data-id="element-73" />
  }, {
    name: 'Papier',
    icon: <PenTool size={20} data-id="element-74" />
  }, {
    name: 'Juwelen',
    icon: <Gem size={20} data-id="element-75" />
  }, {
    name: 'Planten',
    icon: <Flower2 size={20} data-id="element-76" />
  }, {
    name: 'Spelletjes',
    icon: <Dice5 size={20} data-id="element-77" />
  }, {
    name: 'Creativiteit',
    icon: <Sparkles size={20} data-id="element-78" />
  }, {
    name: 'Keramiek',
    icon: <Heart size={20} data-id="element-79" />
  }];
  const toggleTheme = (theme: string) => {
    const updated = selectedThemes.includes(theme) ? selectedThemes.filter(t => t !== theme) : [...selectedThemes, theme];
    onThemeChange(updated);
  };
  const toggleType = (type: string) => {
    const updated = selectedTypes.includes(type) ? selectedTypes.filter(t => t !== type) : [...selectedTypes, type];
    onTypeChange(updated);
  };
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  const getDaysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month: number, year: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };
  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const prevMonthDays = getDaysInMonth(currentMonth === 0 ? 11 : currentMonth - 1, currentMonth === 0 ? currentYear - 1 : currentYear);
  const today = new Date();
  const isCurrentMonth = today.getMonth() === currentMonth && today.getFullYear() === currentYear;
  const todayDate = today.getDate();
  return <aside className="w-full" data-id="element-80">
      {/* Zoeken */}
      <div className="mb-6" data-id="element-81">
        <h3 className="text-sm font-bold mb-2" data-id="element-82">Zoeken</h3>
        <div className="relative" data-id="element-83">
          <input type="text" placeholder="Zoeken..." className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" data-id="element-84" />
        </div>
      </div>

      {/* Datum */}
      <div className="mb-6" data-id="element-85">
        <h3 className="text-sm font-bold mb-2" data-id="element-86">Datum</h3>
        <div className="border border-gray-200 rounded-md p-3 bg-white" data-id="element-87">
          <div className="flex items-center justify-between mb-3" data-id="element-88">
            <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded transition-colors duration-150 focus:outline-none" aria-label="Vorige maand" data-id="element-89">
              <ChevronLeft size={16} data-id="element-90" />
            </button>
            <span className="text-sm font-medium" data-id="element-91">
              {months[currentMonth]}, {currentYear}
            </span>
            <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded transition-colors duration-150 focus:outline-none" aria-label="Volgende maand" data-id="element-92">
              <ChevronRight size={16} data-id="element-93" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0 mb-1" data-id="element-94">
            {['MA', 'DI', 'WO', 'DO', 'VR', 'ZA', 'ZO'].map(day => <div key={day} className="text-center text-xs font-medium text-gray-500 py-1" data-id="element-95">
                {day}
              </div>)}
          </div>

          <div className="grid grid-cols-7 gap-0" data-id="element-96">
            {Array.from({
            length: firstDay
          }, (_, i) => <div key={`prev-${i}`} className="text-center text-xs text-gray-300 py-1.5" data-id="element-97">
                  {prevMonthDays - firstDay + 1 + i}
                </div>)}

            {Array.from({
            length: daysInMonth
          }, (_, i) => {
            const day = i + 1;
            const isToday = isCurrentMonth && day === todayDate;
            return <button key={day} className={`text-center text-xs py-1.5 rounded transition-colors duration-100 focus:outline-none ${isToday ? 'bg-gray-800 text-white font-bold' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-98">
                    {day}
                  </button>;
          })}

            {Array.from({
            length: (7 - (firstDay + daysInMonth) % 7) % 7
          }, (_, i) => <div key={`next-${i}`} className="text-center text-xs text-gray-300 py-1.5" data-id="element-99">
                  {i + 1}
                </div>)}
          </div>
        </div>
      </div>

      {/* Type */}
      <div className="mb-6" data-id="element-100">
        <h3 className="text-sm font-bold mb-2" data-id="element-101">Type</h3>
        <div className="space-y-2" data-id="element-102">
          <label className="flex items-center cursor-pointer" data-id="element-103">
            <input type="checkbox" className="mr-2 h-4 w-4" checked={selectedTypes.includes('maekerscafe')} onChange={() => toggleType('maekerscafe')} data-id="element-104" />
            <span className="text-sm text-gray-700" data-id="element-105">Maekerscafé</span>
          </label>
          <label className="flex items-center cursor-pointer" data-id="element-106">
            <input type="checkbox" className="mr-2 h-4 w-4" checked={selectedTypes.includes('workshop')} onChange={() => toggleType('workshop')} data-id="element-107" />
            <span className="text-sm text-gray-700" data-id="element-108">Workshop</span>
          </label>
        </div>
      </div>

      {/* Thema */}
      <div className="mb-6" data-id="element-109">
        <h3 className="text-sm font-bold mb-2" data-id="element-110">Thema</h3>
        <div className="grid grid-cols-2 gap-2" data-id="element-111">
          {themes.map(theme => {
          const isSelected = selectedThemes.includes(theme.name);
          return <button key={theme.name} onClick={() => toggleTheme(theme.name)} className={`flex flex-col items-center text-center border rounded-lg p-3 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300 ${isSelected ? 'border-gray-500 bg-gray-100' : 'border-gray-200 bg-white hover:bg-gray-50'}`} data-id="element-112">
                <div className="mb-1.5 text-gray-600" data-id="element-113">{theme.icon}</div>
                <span className="text-xs font-medium" data-id="element-114">{theme.name}</span>
              </button>;
        })}
        </div>
      </div>

      {/* Eigenschappen */}
      <div className="mb-6" data-id="element-115">
        <h3 className="text-sm font-bold mb-2" data-id="element-116">Eigenschappen</h3>
        <div className="space-y-2" data-id="element-117">
          <label className="flex items-start cursor-pointer" data-id="element-118">
            <input type="checkbox" className="mt-0.5 mr-2 h-4 w-4" data-id="element-119" />
            <span className="text-sm text-gray-700" data-id="element-120">
              Toon enkel workshops met vrije plaatsen
            </span>
          </label>
          <label className="flex items-start cursor-pointer" data-id="element-121">
            <input type="checkbox" className="mt-0.5 mr-2 h-4 w-4" checked={isEngelstalig} onChange={() => onEngelstaligChange(!isEngelstalig)} data-id="element-122" />
            <span className="text-sm text-gray-700" data-id="element-123">Engelstalig</span>
          </label>
          <label className="flex items-start cursor-pointer" data-id="element-124">
            <input type="checkbox" className="mt-0.5 mr-2 h-4 w-4" checked={isOuderKind} onChange={() => onOuderKindChange(!isOuderKind)} data-id="element-125" />
            <span className="text-sm text-gray-700" data-id="element-126">Ouder-kindworkshop</span>
          </label>
        </div>
      </div>

      {/* Sorteren op */}
      <div className="mb-6" data-id="element-127">
        <h3 className="text-sm font-bold mb-2" data-id="element-128">Sorteren op</h3>
        <select className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300" data-id="element-129">
          <option data-id="element-130">— Sorteren op</option>
          <option data-id="element-131">Datum (oplopend)</option>
          <option data-id="element-132">Datum (aflopend)</option>
          <option data-id="element-133">Naam (A-Z)</option>
          <option data-id="element-134">Naam (Z-A)</option>
        </select>
      </div>

      {/* Action buttons */}
      <div className="space-y-2" data-id="element-135">
        <Button variant="primary" size="medium" className="w-full" data-id="element-136">
          Filters toepassen
        </Button>
        <Button variant="secondary" size="medium" className="w-full" onClick={() => {
        onTypeChange([]);
        onThemeChange([]);
        onOuderKindChange(false);
        onEngelstaligChange(false);
      }} data-id="element-137">
          Reset filters
        </Button>
      </div>
    </aside>;
}